import { Container, Sprite, TextStyle, Text, IDestroyOptions } from "pixi.js";
import { UserProfileResourcePackage } from "../resources/UserProfileResourcePackage";
import { ProgressBar } from "@pixi/ui";
import { IPlayerModel } from "../../../common/models/IPlayerModel";
import { IUserLevelUpModel } from "../../userlevelup/model/IUserLevelUpModel";
import { gsap } from "gsap";
import { nameof } from "bindable-data";
import { GameFont } from "../../../common/GameFont";
import { StringFormatExtention } from "../../../../../extensions/StringFormatExtention";

export class LevelProgressContainer extends Container {
    private static readonly COLLECT_POINTS_ANIMATION_TIME = 1;

    private readonly userProfileResources: UserProfileResourcePackage;
    private readonly playerModel: IPlayerModel;
    private readonly userLevelUpModel: IUserLevelUpModel;
    private progressBar: ProgressBar;
    private currentLevel: Text;
    private nextLevel: Text;
    private expCount: Text;
    private currentPointsBalance: number;

    public constructor(userProfileResources: UserProfileResourcePackage, playerModel: IPlayerModel, userLevelUpModel: IUserLevelUpModel) {
        super();
        this.userProfileResources = userProfileResources;
        this.playerModel = playerModel;
        this.userLevelUpModel = userLevelUpModel;
        this.currentPointsBalance = this.playerModel.upgradePointsBalance.value;
        this.build();
        this.playerModel.propertyChanged.add(this.onPlayerModelPropertyChanged, this);
        this.once("added", this.show.bind(this));
    }

    private async onPlayerModelPropertyChanged(model: IPlayerModel, key: string): Promise<void> {
        switch (key) {
            case nameof(model, "level"):
                this.currentLevel.text = `LV.${model.level.value}`;
                break;
            case nameof(model, "nextLevel"):
                if (model.nextLevel.value == undefined) {
                    this.nextLevel.visible = false;
                }
                this.nextLevel.text = `LV.${model.nextLevel.value || 0}`;
                break;
            case nameof(model, "upgradePointsBalance"):
            case nameof(model, "requiredPointsBalance"):
                this.updateProgress(model.upgradePointsBalance.value);
                break;
        }
    }

    private build(): void {
        const progressBarBg = new Sprite(this.userProfileResources.assets_views_user_profile_progress_bg_png);
        const progressBarFill = new Sprite(this.userProfileResources.assets_views_user_profile_progress_png);
        const currentProgressValue = this.playerModel.requiredPointsBalance.value != undefined ?
            this.playerModel.upgradePointsBalance.value / this.playerModel.requiredPointsBalance.value * 100 : 100;
        this.progressBar = new ProgressBar({ bg: progressBarBg, fill: progressBarFill, progress: currentProgressValue });
        this.progressBar.pivot.set(this.progressBar.width / 2, this.progressBar.height / 2);
        this.addChild(this.progressBar);

        this.expCount = new Text(`${StringFormatExtention.formatWithComma(this.playerModel.upgradePointsBalance.value)}/${StringFormatExtention.formatWithComma(this.playerModel.requiredPointsBalance.value || 0)} EXP`, new ExpCountTextStyle());
        this.expCount.anchor.set(0.5, 1);
        this.expCount.position.set(this.progressBar.x, -63);
        this.addChild(this.expCount);

        this.currentLevel = new Text(`LV.${this.playerModel.level.value}`, new LevelTextStyle());
        this.currentLevel.anchor.set(0.5, 1);
        this.currentLevel.position.set(this.progressBar.x - this.progressBar.width * 0.5, -66);
        this.addChild(this.currentLevel);

        this.nextLevel = new Text(`LV.${this.playerModel.nextLevel.value || 0}`, new LevelTextStyle());
        this.nextLevel.anchor.set(0.5, 1);
        this.nextLevel.position.set(this.progressBar.x + this.progressBar.width * 0.5, -66);
        this.addChild(this.nextLevel);
    }

    private show(): void {
        this.onPlayerModelPropertyChanged(this.playerModel, nameof(this.playerModel, "nextLevel"));
        this.onPlayerModelPropertyChanged(this.playerModel, nameof(this.playerModel, "upgradePointsBalance"));
    }

    public async updateProgressAnimation(): Promise<void> {
        const animationTimeLine = new gsap.core.Timeline();
        const target = { value: this.currentPointsBalance };
        animationTimeLine.to(target, { value: this.userLevelUpModel.playerDto.upgradePointsBalance, delay: 0.5, onUpdate: () => this.updateProgress(target.value), duration: LevelProgressContainer.COLLECT_POINTS_ANIMATION_TIME, ease: "power1.in" });
        await animationTimeLine;
        animationTimeLine.kill();
    }

    private updateProgress(value: number): void {
        this.expCount.text = `${StringFormatExtention.formatWithComma(Math.round(value))}/${StringFormatExtention.formatWithComma(this.playerModel.requiredPointsBalance.value || value)} XP`;
        this.progressBar.progress = this.playerModel.requiredPointsBalance.value != undefined ? value / this.playerModel.requiredPointsBalance.value * 100 : 100;
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.playerModel.propertyChanged.remove(this.onPlayerModelPropertyChanged, this);
        super.destroy(options);
    }
}

class ExpCountTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fill: "#ffffff",
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 48
        }));
    }
}

class LevelTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fill: "#ffffff",
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 40
        }));
    }
}
